<script setup lang="ts">
    import KButton from "@/components/Button.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    //@ts-ignore
    import PrimeDialog from "primevue/dialog/dialog.common";
    import { ref, toRefs, watch } from "vue";

    const emit = defineEmits<{
        (event: "show"): void;
        (event: "hide"): void;
        (event: "accept"): void;
        (event: "cancel"): void;
    }>();

    const props = withDefaults(
        defineProps<{
            /**
             * Ska dialogen visas?
             * OBS! Hantering av när den ska visas kan göras via ref.showDialog istället för reaktiv prop
             */
            show?: boolean;
            /**
             * Placering av dialog
             */
            position?:
                | "center"
                | "top"
                | "bottom"
                | "left"
                | "right"
                | "topleft"
                | "topright"
                | "bottomleft"
                | "bottomright";
            /**
             * Ska dialogen stängas vid escape?
             */
            closeOnEscape?: boolean;
            /**
             * Ska den agera som en modal?
             */
            modal?: boolean;
            /**
             * Ska dialogen stängas vid klick på backdrop?
             */
            closeOnBackdropClick?: boolean;
            /**
             * Ska dialogen kunna stängas via klick på backdrop eller kryss?
             * Dialogen behöver stängas via ref om satt till false
             */
            closable?: boolean;
            /**
             * Ska dialogen kunna maximeras?
             */
            maximizable?: boolean;
            /**
             * Ska headern i dialogen visas?
             */
            showHeader?: boolean;
            variant?: "basic" | "warning" | "action" | "info";
            width?: "default" | "medium" | "large";
            /**
             * Innehåll i accept-knappen (ofta den som används för submit). Tomt content kommer inte rendera någon
             * accept-knapp
             */
            acceptContent?: string;
            /**
             * Innehåll i cancel-knappen (ofta den som används för avbryt). Tomt content kommer inte rendera någon
             * avbryt-knapp
             */
            cancelContent?: string;
            /**
             * Button.vue size prop för default submit button
             */
            acceptSize?: string;
            /**
             * Button.vue variant prop för default submit button
             */
            acceptVariant?: string;
            /**
             * Button.vue size prop för default cancel button
             */
            cancelSize?: string;
            /**
             * Button.vue variant prop för default cancel button
             */
            cancelVariant?: string;
            /**
             * Funktion som kommer anropas vid klick på accept-knappen.
             * Laddningsanimation kommer automatiskt visas på knappen tills anropet är klart
             * Funktionen kan returnera en boolean för att indikera om dialogen ska stängas eller inte
             */
            acceptAction?: () => void | Promise<boolean>;
            /**
             * Funktion som kommer anropas vid klick på cancel-knappen. Funktionen kan returnera en boolean
             * för att indikera om dialogen ska stängas eller inte
             */
            cancelAction?: () => void | Promise<boolean>;
            /**
             * Titeln inom content. Renderas nedanför headern.
             */
            contentTitle?: string;
            /**
             * Innehåll i headern
             */
            headerContent?: string;
            /**
             * Titeln i headern
             */
            headerTitle?: string;
            /**
             * Innehåll i headern som renderas nedanför resten av header-blocket
             */
            subHeaderContent?: string;
            zIndex?: number;
        }>(),
        {
            show: false,
            position: "center",
            closeOnEscape: true,
            modal: true,
            closeOnBackdropClick: true,
            closable: false,
            maximizable: false,
            showHeader: true,
            variant: "basic",
            acceptSize: "primary",
            acceptVariant: "standard",
            cancelSize: "secondary",
            cancelVariant: "contour",
            width: "default",
        }
    );

    const { show, acceptAction, cancelAction } = toRefs(props);

    const internalShow = ref(show.value);
    const dialogRef = ref();
    const isLoading = ref(false);

    const showDialog = () => {
        internalShow.value = true;
    };

    const hideDialog = () => {
        internalShow.value = false;
    };

    defineExpose({
        show: showDialog,
        hide: hideDialog,
    });

    const onShow = () => {
        emit("show");
    };

    const onHide = () => {
        emit("hide");
    };

    const onAccept = async () => {
        try {
            if (acceptAction?.value) {
                isLoading.value = true;

                const accept = await acceptAction.value();
                if (typeof accept === "boolean" && !accept) {
                    return;
                }
            }

            emit("accept");
            hideDialog();
        } catch (error) {
            console.log(error);
        } finally {
            isLoading.value = false;
        }
    };

    const onCancel = async () => {
        try {
            if (cancelAction?.value) {
                const cancel = await cancelAction.value();
                if (typeof cancel === "boolean" && !cancel) {
                    return;
                }
            }

            emit("cancel");
            hideDialog();
        } catch (error) {
            console.log(error);
        } finally {
            isLoading.value = false;
        }
    };

    watch(show, (newVisible) => {
        internalShow.value = newVisible;
    });
</script>

<template>
    <portal v-if="internalShow" to="modal">
        <PrimeDialog
            ref="dialogRef"
            :visible.sync="internalShow"
            @show="onShow"
            @hide="onHide"
            :class="['k-dialog', `variant-${variant}`, `width-${width}`]"
            :closable="closable || variant === 'action'"
            :maximizable="maximizable"
            :showHeader="showHeader"
            :closeOnEscape="closeOnEscape"
            :modal="modal"
            :dismissableMask="closeOnBackdropClick"
        >
            <template v-if="showHeader" #header>
                <div
                    v-if="$slots.header || headerContent || headerTitle || ['warning', 'info'].includes(variant)"
                    class="header-wrapper"
                >
                    <div class="header">
                        <h1 v-if="headerTitle" class="header-title">{{ headerTitle }}</h1>
                        <slot name="header" :isLoading="isLoading">
                            <template v-if="variant === 'warning'">
                                <h1 aria-hidden="false">Varning</h1>
                                <div class="circle">
                                    <img src="@/assets/dialogs/warning.svg" alt="Varning" />
                                </div>
                            </template>
                            <template v-else-if="variant === 'info'">
                                <h1 aria-hidden="false">Information</h1>
                                <div class="circle">
                                    <img src="@/assets/dialogs/information.svg" alt="Information" />
                                </div>
                            </template>
                        </slot>
                    </div>
                    <div v-if="$slots.subHeader || subHeaderContent" class="sub-header">
                        <p>{{ subHeaderContent }}</p>
                    </div>
                </div>
            </template>
            <div class="content-wrapper">
                <div v-if="$slots.contentTitle || contentTitle" class="content-title">
                    <slot name="contentTitle" :isLoading="isLoading">
                        <template v-if="contentTitle">
                            <template
                                v-if="$slots.contentTitle || contentTitle || ['warning', 'info'].includes(variant)"
                            >
                                <h2>{{ contentTitle }}</h2>
                            </template>
                            <template v-else>
                                <h1>{{ contentTitle }}</h1>
                            </template>
                        </template>
                    </slot>
                </div>
                <div class="content">
                    <slot :isLoading="isLoading" />
                </div>
            </div>
            <template v-if="$slots.footer || $slots.actions || acceptContent || cancelContent" #footer>
                <div class="footer">
                    <slot name="footer">
                        <div v-if="$slots.actions || acceptContent || cancelContent" class="actions">
                            <ButtonGroup v-if="acceptContent || cancelContent" margin="none" class="default-actions">
                                <slot name="actions" :isLoading="isLoading"></slot>
                                <KButton
                                    v-if="cancelContent"
                                    type="button"
                                    @click="onCancel"
                                    :size="cancelSize"
                                    :variant="cancelVariant"
                                    :disabled="isLoading"
                                >
                                    {{ cancelContent }}
                                </KButton>
                                <KButton
                                    v-if="acceptContent"
                                    type="button"
                                    @click="onAccept"
                                    :size="acceptSize"
                                    :variant="acceptVariant"
                                    :isLoading="isLoading"
                                >
                                    {{ acceptContent }}
                                </KButton>
                            </ButtonGroup>
                        </div>
                    </slot>
                </div>
            </template>
        </PrimeDialog>
    </portal>
</template>

<style lang="scss" scoped>
    @import "@/style/typography";

    .k-dialog {
        $horizontal-padding: 3rem;
        container-name: dialog;
        container-type: size;

        ::v-deep > .p-dialog {
            &.width-default {
                width: 37rem;
            }

            &.width-medium {
                width: 48rem;
            }

            &.width-large {
                width: 60rem;
            }

            font-family: Roboto, sans-serif;
            height: fit-content;
            max-height: 96dvh;
            max-width: 96dvw;

            .p-dialog-header {
                padding: 0;

                .p-dialog-header-icons {
                    position: absolute;
                    right: 1.5rem;
                    top: 1.5rem;

                    .p-dialog-header-icon {
                        border-radius: $border-radius-max;

                        &:enabled:hover {
                            background-color: $color-button-standard-hover;
                        }

                        .p-dialog-header-close-icon {
                            display: grid;
                            place-items: center;
                            width: 100%;
                            height: 100%;

                            &::before {
                                display: block;
                                content: url("@/assets/close_white.svg");
                                width: 100%;
                                height: auto;
                            }
                        }
                    }
                }
            }

            .p-dialog-content {
                padding: 0;
            }

            .p-dialog-footer {
                padding: 0;
            }
        }

        // Primes dialog är dum och sätter inskickad klass på backdrop och inre container för content
        > .k-dialog {
            .header-wrapper {
                width: 100%;
                height: 100%;

                .header {
                    display: grid;
                    background-color: $color-default-blue;
                    padding: 1.5rem;
                    place-items: center;
                    margin-bottom: 2rem;

                    .circle {
                        display: grid;
                        place-items: center;
                        background-color: $color-light;
                        border-radius: 100%;
                        width: 100px;
                        height: 100px;
                    }
                }

                .header-title {
                    @extend h1;
                    color: $color-light;
                    margin-bottom: 0;
                }

                .sub-header {
                    padding-left: $horizontal-padding;
                    padding-right: $horizontal-padding;
                    margin-bottom: 1.5rem;
                }
            }

            &.width-default .content-wrapper {
                padding-inline: 3rem;
            }
            &.width-medium .content-wrapper {
                padding-inline: 6.7rem;
            }
            &.width-large .content-wrapper {
                padding-inline: 9rem;
            }

            .content-wrapper {
                display: flex;
                flex-direction: column;
                padding: 0 $horizontal-padding 2rem $horizontal-padding;
                overflow: auto;
                min-height: 10rem;
                max-height: fit-content;

                .content-title {
                    h2,
                    h1 {
                        @extend h1;
                        color: $color-text-popup;
                        margin-bottom: 0;
                        line-height: 1.3;
                    }
                }

                .content {
                    // Om content har något innehåll
                    &:has(*) {
                        margin-top: 1.5rem;
                    }
                }
            }

            .footer {
                display: grid;
                padding: 0.5rem $horizontal-padding 1.5rem $horizontal-padding;

                .actions {
                    display: flex;
                    justify-self: flex-end;

                    .button {
                        margin: 0;
                    }
                }
            }

            &.variant-warning {
                .header {
                    background-color: $dialog-warning;
                    justify-items: center;
                }
            }

            &.variant-action {
                .footer {
                    .actions {
                        justify-self: flex-start;
                    }
                }
            }
        }
    }
</style>
